// Per-row blood drip drain. Reuses the existing `.bleed-blobs` infrastructure
// (the same curtain + wavy edge that fires on `.absent` tiles), just slowed
// to `--drain-ms` and synchronized across the row. Each tile's per-tile
// randomization (width, left, --liquid-from, edge shape) is preserved — we
// only override the animation timing.
//
// On drain, the `.draining` class stays on the tiles so the curtain holds at
// its end state through the flip-less reveal and into `.absent`. Because the
// `.tile.draining .liquid` rule sits AFTER `.tile.absent .liquid` in the
// stylesheet, the cascade keeps the slow animation values in place for
// drained-then-absent tiles — no restart, no visual snap.

type SavedLiquid = {
  liquid: HTMLElement;
  duration: string;
  delay: string;
  liquidFrom: string;
};

let drainTiles: HTMLElement[] = [];
let savedLiquids: SavedLiquid[] = [];
let drained = false;
let drainCb: (() => void) | null = null;
let firstLiquid: HTMLElement | null = null;
let onAnimEnd: (() => void) | null = null;

// Single visibility listener installed at module load. Pauses the drip while
// the tab is hidden so a chat notification doesn't auto-fail the row.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) pauseDrip();
    else resumeDrip();
  });
}

export function startRowDrip(
  boardEl: HTMLElement,
  rowIdx: number,
  durationMs: number,
  elapsedMs: number,
  onDrain: () => void,
): void {
  cancelDrip();
  const row = boardEl.querySelectorAll<HTMLElement>('.row')[rowIdx];
  if (!row) return;
  const tiles = Array.from(row.querySelectorAll<HTMLElement>('.tile'));
  if (tiles.length === 0) return;
  drainTiles = tiles;
  drainCb = onDrain;
  drained = false;

  for (const tile of tiles) {
    // The board's randomizeBleed sets animation-duration + animation-delay
    // inline per tile to vary the .absent timing. Inline wins over CSS, so we
    // clear those properties here and let the .tile.draining CSS rule (which
    // uses var(--drain-ms) / var(--drain-delay)) take over. Originals are
    // saved so a pre-drain cancel (player submitted a guess) can restore the
    // randomization for the subsequent .absent state.
    const liquid = tile.querySelector<HTMLElement>('.bleed-blobs .liquid');
    if (liquid) {
      savedLiquids.push({
        liquid,
        duration: liquid.style.animationDuration,
        delay: liquid.style.animationDelay,
        liquidFrom: liquid.style.getPropertyValue('--liquid-from'),
      });
      liquid.style.animationDuration = '';
      liquid.style.animationDelay = '';
      // Push the curtain bottom AND its wavy leading edge (22px tall, sits
      // below the curtain) fully above the tile at animation start. -28 px
      // covers the wave's max depth (~20.5 px) plus the goo filter's ~5 px
      // blur expansion, so each tile begins with zero visible red. As the
      // animation progresses, the wavy edge slides in first, giving each
      // tile its own randomized wave shape — that's where per-tile variety
      // comes from while keeping all five tiles synchronized on the timer.
      liquid.style.setProperty('--liquid-from', '-28px');
    }
    tile.style.setProperty('--drain-ms', `${durationMs}ms`);
    tile.style.setProperty('--drain-delay', `-${elapsedMs}ms`);
    tile.classList.add('draining');
  }

  // If the saved elapsed already exceeds the duration (player reloads after
  // the row should have drained), fire immediately so state catches up.
  if (elapsedMs >= durationMs) {
    drained = true;
    onDrain();
    return;
  }

  // Drain signal: animationend on the first tile's .liquid. All five tiles
  // share the same duration and delay, so they complete simultaneously and
  // one listener is enough.
  const first = tiles[0]?.querySelector<HTMLElement>('.bleed-blobs .liquid');
  if (first) {
    firstLiquid = first;
    onAnimEnd = () => {
      if (drained) return;
      drained = true;
      drainCb?.();
    };
    first.addEventListener('animationend', onAnimEnd);
  }
}

export function cancelDrip(): void {
  if (firstLiquid && onAnimEnd) {
    firstLiquid.removeEventListener('animationend', onAnimEnd);
  }
  firstLiquid = null;
  onAnimEnd = null;

  // After a drain, leave .draining + the slow-animation overrides in place.
  // The curtain stays at scaleY(1)/translateY(0) via animation-fill-mode:
  // forwards, so the row reads as "the killer got you" through the reveal
  // and into the .absent state without any visual restart.
  if (!drained) {
    for (const tile of drainTiles) {
      tile.classList.remove('draining', 'paused');
      tile.style.removeProperty('--drain-ms');
      tile.style.removeProperty('--drain-delay');
    }
    for (const { liquid, duration, delay, liquidFrom } of savedLiquids) {
      liquid.style.animationDuration = duration;
      liquid.style.animationDelay = delay;
      if (liquidFrom) liquid.style.setProperty('--liquid-from', liquidFrom);
      else liquid.style.removeProperty('--liquid-from');
    }
  }

  savedLiquids = [];
  drainTiles = [];
  drainCb = null;
  drained = false;
}

export function pauseDrip(): void {
  if (drained) return;
  for (const tile of drainTiles) tile.classList.add('paused');
}

export function resumeDrip(): void {
  if (drained) return;
  for (const tile of drainTiles) tile.classList.remove('paused');
}

export function isDripRunning(): boolean {
  return drainTiles.length > 0 && !drained;
}

// Player submitted a guess while the drip was still descending. Accelerate
// the live animation so the curtain rushes from its current position to
// the bottom in ~targetMs, rather than letting cancelDrip rip off .draining
// (which lets .absent's fast 2.8s animation restart from the top).
//
// We detach the animationend listener first so the accelerated completion
// doesn't fire drainCb (which would record the guess as all-absent). The
// drained flag is set to true so the tile state is held — same as a real
// drain. Pre-existing tiles are NOT cleaned up; they stay at the
// translateY(0) end state, and any later result class (correct/present)
// hides the curtain via the .draining.correct / .draining.present CSS.
export function accelerateDrip(targetMs = 400): void {
  if (drainTiles.length === 0) return;

  if (firstLiquid && onAnimEnd) {
    firstLiquid.removeEventListener('animationend', onAnimEnd);
  }
  firstLiquid = null;
  onAnimEnd = null;
  drainCb = null;
  drained = true;

  for (const tile of drainTiles) {
    const liquid = tile.querySelector<HTMLElement>('.bleed-blobs .liquid');
    if (!liquid) continue;
    for (const anim of liquid.getAnimations()) {
      if (!anim.effect) continue;
      const timing = (anim.effect as KeyframeEffect).getComputedTiming();
      const current = Number(anim.currentTime ?? 0);
      const duration = Number(timing.duration ?? 0);
      const remaining = Math.max(0, duration - current);
      if (remaining > targetMs) anim.playbackRate = remaining / targetMs;
    }
  }
}
